/**
 * Add/edit dialog for a CONDITIONAL routing rule.
 *
 * Progressive disclosure keeps the form light: the core flow is
 * name -> when (trigger states + optional conditions) -> who gets it.
 * Everything else (run a workflow action afterwards, notify, active)
 * hides behind a single "More options" toggle.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Switch } from '@/shared/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { ConditionBuilder } from '@/admin/components/condition-builder'
import { useAvailableRefs } from '@/admin/components/condition-builder/use-available-refs'
import type { PredicateNode } from '@/admin/components/condition-builder/types'
import { toastApiError } from '@/shared/lib/api-error'
import { useForm, type FormField } from '@/shared/lib/forms'
import {
  useCreateRoutingRule,
  useUpdateRoutingRule,
  type RoutingMeta,
  type RoutingRule,
  type RoutingStrategy,
} from '@/admin/hooks/use-routing-rules'
import {
  buildAssigneeFields,
  resolveAssigneeFromForm,
  useMemberOptions,
} from './routing-helpers'

interface ConditionalRuleDialogProps {
  editing: RoutingRule | null
  meta: RoutingMeta
  onClose: () => void
}

export function ConditionalRuleDialog({
  editing,
  meta,
  onClose,
}: ConditionalRuleDialogProps) {
  const { t } = useTranslation()
  const isEdit = editing !== null
  const create = useCreateRoutingRule()
  const update = useUpdateRoutingRule()
  const pending = create.isPending || update.isPending

  const { refs, byRef } = useAvailableRefs({ entityKey: 'claim' })
  const personOptions = useMemberOptions()
  const roleOptions = useMemo(
    () => meta.roles.map((role) => ({ value: role.name, label: role.name })),
    [meta.roles],
  )
  const zoneRoleOptions = useMemo(
    () => (meta.zone_roles ?? []).map((r) => ({ value: r.name, label: r.name })),
    [meta.zone_roles],
  )
  const stateLabel = useMemo(() => {
    const byKey = new Map(meta.state_options.map((s) => [s.key, s.label]))
    return (key: string) => byKey.get(key) ?? key
  }, [meta.state_options])

  // Per-workflow scoping. A rule targets one claim workflow; its trigger
  // states + actions come from that workflow only. When the tenant has a
  // single claim workflow there is no picker and the rule stays unscoped
  // ('' = all), but the state options are still that one workflow's states.
  const workflows = useMemo(() => meta.workflows ?? [], [meta.workflows])
  const multiWorkflow = workflows.length > 1
  const [workflowKey, setWorkflowKey] = useState(
    editing?.workflow_key || workflows[0]?.key || '',
  )
  const selectedWorkflow =
    workflows.find((w) => w.key === workflowKey) ?? workflows[0]
  const scopedStates = selectedWorkflow?.states ?? meta.state_options
  const scopedActions = selectedWorkflow?.actions ?? meta.action_options

  // The condition predicate lives outside the generated form (it is the
  // shared ConditionBuilder, not a generated field); submit reads it.
  const editingConditions =
    editing?.conditions && Object.keys(editing.conditions).length > 0
      ? (editing.conditions as PredicateNode)
      : null
  const [conditions, setConditions] = useState<PredicateNode | null>(
    editingConditions,
  )

  const hadExtras = Boolean(
    editing &&
      (editing.target_action_key ||
        !editing.notify ||
        !editing.is_active ||
        editing.exclude_creator ||
        editing.exclude_current_assignee),
  )

  const fields = useMemo<FormField[]>(() => {
    const initialKind = editing?.assignee.kind ?? 'role'
    const actionOptions = [
      { label: 'Do nothing', value: '' },
      ...scopedActions.map((action) => ({
        label: action.source_state_key
          ? `${action.label} (from ${stateLabel(action.source_state_key)})`
          : action.label,
        value: action.key,
      })),
    ]
    return [
      {
        type: 'text',
        name: 'name',
        label: 'Rule name',
        placeholder: 'e.g. Escalate large approvals',
        value: editing?.name ?? '',
        testId: 'routing-rule-name',
        validationSchema: z.string().trim().min(1, 'Name the rule.'),
      },
      ...(multiWorkflow
        ? ([
            {
              type: 'custom',
              name: '_workflow',
              value: '',
              render: () => (
                <div className="flex flex-col gap-1.5">
                  <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                    Claim workflow
                  </Label>
                  <Select value={workflowKey} onValueChange={setWorkflowKey}>
                    <SelectTrigger data-testid="routing-workflow">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {workflows.map((w) => (
                        <SelectItem key={w.key} value={w.key}>
                          {w.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ),
            },
          ] as FormField[])
        : []),
      {
        type: 'multiSelect',
        name: 'trigger_states',
        label: 'Apply when the claim reaches',
        value: (editing?.trigger_states ?? []).filter((k) =>
          scopedStates.some((s) => s.key === k),
        ),
        options: scopedStates.map((state) => ({
          label: state.label,
          value: state.key,
        })),
      },
      {
        type: 'checkbox',
        name: 'trigger_on_creation',
        label: 'Also apply when a claim is first created',
        value: editing?.trigger_on_creation ?? false,
      },
      {
        type: 'custom',
        name: '_conditions',
        value: '',
        render: () => (
          <div className="flex flex-col gap-1.5">
            <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              And only when (conditions)
            </Label>
            <ConditionBuilder
              value={conditions}
              onChange={setConditions}
              availableRefs={refs}
              byRef={byRef}
            />
          </div>
        ),
      },
      ...buildAssigneeFields({
        roleOptions,
        personOptions: personOptions ?? [],
        strategyOptions: meta.strategy_options,
        zoneRoleOptions,
        initial: {
          kind: initialKind,
          role: initialKind === 'role' ? (editing?.assignee.value ?? '') : '',
          person:
            initialKind === 'person' ? (editing?.assignee.value ?? '') : '',
          zoneRole:
            initialKind === 'zone' ? (editing?.assignee.value ?? '') : '',
          strategy: editing?.strategy || 'least_busy',
          prefer_creator: editing?.prefer_creator ?? false,
        },
      }),
      {
        type: 'custom',
        name: 'show_more',
        value: hadExtras,
        render: (formContext) => {
          const open = Boolean(formContext.values.show_more)
          const Icon = open ? ChevronDown : ChevronRight
          return (
            <Button
              variant="ghost"
              type="button"
              onClick={() => formContext.setFieldValue('show_more', !open)}
              className="h-auto w-full justify-start gap-2 rounded-[6px] border-[0.5px] border-dashed border-[var(--rule)] px-3 py-2 text-left text-[12px] text-[var(--ink-2)]"
            >
              <Icon className="h-3.5 w-3.5" />
              More options - run an action, notifications, pause
            </Button>
          )
        },
      },
      {
        type: 'select',
        name: 'target_action_key',
        label: 'Then run a workflow action (moves the claim on)',
        value: editing?.target_action_key ?? '',
        testId: 'routing-target-action',
        options: actionOptions,
        visible: (ctx) => Boolean(ctx.values.show_more),
      },
      {
        type: 'checkbox',
        name: 'notify',
        label: 'Email the assignee a link to the claim',
        value: editing?.notify ?? true,
        visible: (ctx) => Boolean(ctx.values.show_more),
      },
      {
        type: 'checkbox',
        name: 'exclude_creator',
        label: "Never assign to the claim's creator (segregation of duties)",
        value: editing?.exclude_creator ?? false,
        visible: (ctx) =>
          Boolean(ctx.values.show_more) && ctx.values.assignee_kind === 'role',
      },
      {
        type: 'checkbox',
        name: 'exclude_current_assignee',
        label: "Never assign to the claim's current handler",
        value: editing?.exclude_current_assignee ?? false,
        visible: (ctx) =>
          Boolean(ctx.values.show_more) && ctx.values.assignee_kind === 'role',
      },
      {
        type: 'checkbox',
        name: 'only_if_unassigned',
        label: t(
          'routing.rule.onlyIfUnassigned',
          'Only assign if the claim has no owner yet (never overwrite)',
        ),
        value: editing?.only_if_unassigned ?? false,
        visible: (ctx) => Boolean(ctx.values.show_more),
      },
      {
        type: 'custom',
        name: 'is_active',
        value: editing?.is_active ?? true,
        visible: (ctx) => Boolean(ctx.values.show_more),
        render: (formContext) => {
          const active = Boolean(formContext.values.is_active)
          return (
            <Button
              variant="ghost"
              type="button"
              onClick={() => formContext.setFieldValue('is_active', !active)}
              className="h-auto w-full justify-start gap-3 whitespace-normal rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2 text-left hover:border-[var(--green-border)]"
            >
              <Switch checked={active} decorative />
              <span className="block text-[12px] text-[var(--ink)]">
                {active ? 'Rule active' : 'Rule paused'}
              </span>
            </Button>
          )
        },
      },
    ]
  }, [
    t,
    editing,
    meta.strategy_options,
    scopedStates,
    scopedActions,
    multiWorkflow,
    workflows,
    workflowKey,
    roleOptions,
    personOptions,
    zoneRoleOptions,
    stateLabel,
    conditions,
    refs,
    byRef,
    hadExtras,
  ])

  const { Form } = useForm(
    { name: 'conditional-rule-form', submit: 'footer', fields },
    async (raw) => {
      const values = raw as Record<string, unknown>
      const triggerStates = Array.isArray(values.trigger_states)
        ? (values.trigger_states as string[])
        : []
      const triggerOnCreation = Boolean(values.trigger_on_creation)
      if (!triggerStates.length && !triggerOnCreation) {
        toast.error(
          'Pick at least one trigger - a workflow state or claim creation.',
        )
        return
      }
      const { kind, value: target } = resolveAssigneeFromForm(values)
      // Zone routing needs no field predicate - the state trigger + geo
      // lookup are the condition. Every other conditional rule does.
      if (!conditions && kind !== 'zone') {
        toast.error('Add at least one condition, or use a direct rule instead.')
        return
      }
      if (!target) {
        toast.error(
          kind === 'role'
            ? 'Pick the role the claim routes to.'
            : kind === 'zone'
              ? 'Pick the zone-owner role.'
              : 'Pick the person the claim routes to.',
        )
        return
      }
      const isRole = kind === 'role'
      const payload = {
        kind: 'conditional' as const,
        name: String(values.name).trim(),
        is_active: Boolean(values.is_active),
        assignee: { kind, value: target },
        strategy: isRole
          ? (String(values.strategy) as RoutingStrategy)
          : undefined,
        prefer_creator: isRole ? Boolean(values.prefer_creator) : false,
        exclude_creator: isRole ? Boolean(values.exclude_creator) : false,
        exclude_current_assignee: isRole
          ? Boolean(values.exclude_current_assignee)
          : false,
        notify: Boolean(values.notify),
        only_if_unassigned: Boolean(values.only_if_unassigned),
        conditions: conditions ?? undefined,
        trigger_on_creation: triggerOnCreation,
        trigger_states: triggerStates,
        target_action_key: String(values.target_action_key ?? ''),
        workflow_key: multiWorkflow ? workflowKey : '',
      }
      try {
        if (isEdit) {
          await update.mutateAsync({ uuid: editing.uuid, ...payload })
        } else {
          await create.mutateAsync(payload)
        }
        toast.success('Routing rule saved')
        onClose()
      } catch (err) {
        toastApiError(err, 'Failed to save the routing rule')
      }
    },
    [fields, editing, isEdit, conditions, multiWorkflow, workflowKey],
  )

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-[640px] overflow-y-auto sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit conditional routing rule' : 'Add conditional routing rule'}
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[var(--ink-3)]">
            Re-route a claim to a role or person when it reaches a state and
            your conditions match - claim fields, amounts, host address, and
            more.
          </DialogDescription>
        </DialogHeader>
        {personOptions ? (
          <Form className="space-y-5" />
        ) : (
          <Loader2 className="mx-auto h-5 w-5 animate-spin text-[var(--ink-3)]" />
        )}
        <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)] pt-3">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="conditional-rule-form"
            disabled={pending}
            data-testid="conditional-rule-submit"
          >
            {pending ? 'Saving...' : 'Save rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
